<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <h2 class="text-lg font-bold text-secondary-900 dark:text-white">Firmas de correo</h2>
        <div class="flex items-center gap-2">
          <button
            @click="openEditor(null)"
            class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
          >
            <Plus class="w-4 h-4" />
            <span>Nueva firma</span>
          </button>
          <button @click="emit('close')" class="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
            <X class="w-5 h-5 text-secondary-500" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <SignatureList
          :signatures="signatureStore.userSignatures"
          :is-loading="signatureStore.isLoading"
          @edit="openEditor"
          @delete="confirmDelete"
          @set-default="handleSetDefault"
        />
      </div>
    </div>

    <!-- Signature Editor -->
    <SignatureEditor
      v-if="showEditor"
      :signature="signatureToEdit"
      @close="showEditor = false"
      @saved="handleSaved"
    />

    <!-- Delete Confirmation -->
    <ConfirmModal
      :is-open="showDeleteModal"
      title="Eliminar firma"
      :message="`¿Estás seguro de que quieres eliminar la firma '${signatureToDelete?.name}'? Esta acción no se puede deshacer.`"
      confirm-text="Eliminar"
      cancel-text="Cancelar"
      variant="danger"
      @confirm="handleDelete"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Plus, X } from 'lucide-vue-next'
import { useEmailSignatureStore } from '@/stores/emailSignature'
import { useUiStore } from '@/stores'
import SignatureEditor from './SignatureEditor.vue'
import SignatureList from './SignatureList.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import type { EmailSignature } from '@/types/emailSignature'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const signatureStore = useEmailSignatureStore()
const uiStore = useUiStore()

const showEditor = ref(false)
const signatureToEdit = ref<EmailSignature | null>(null)
const showDeleteModal = ref(false)
const signatureToDelete = ref<EmailSignature | null>(null)

function openEditor(signature: EmailSignature | null) {
  signatureToEdit.value = signature
  showEditor.value = true
}

function handleSaved(_signature: EmailSignature) {
  showEditor.value = false
  uiStore.showSuccess('Firma guardada', 'La firma se ha guardado correctamente')
}

async function handleSetDefault(signature: EmailSignature) {
  const success = await signatureStore.setAsDefault(signature.id)
  if (success) {
    uiStore.showSuccess('Firma predeterminada', `"${signature.name}" es ahora tu firma predeterminada`)
  }
}

function confirmDelete(signature: EmailSignature) {
  signatureToDelete.value = signature
  showDeleteModal.value = true
}

async function handleDelete() {
  if (!signatureToDelete.value) return
  const success = await signatureStore.deleteSignature(signatureToDelete.value.id)
  if (success) {
    uiStore.showSuccess('Firma eliminada', 'La firma ha sido eliminada')
  }
  showDeleteModal.value = false
  signatureToDelete.value = null
}
</script>
